// AnalyticsControls — port of design/v3/AnalyticsControls.jsx (controls bar only).

import type { ChangeEvent } from 'react';
import { Icon } from '../primitives';

export type Period = '7d' | '30d' | 'mom' | 'yoy';
export type Compare = 'prev' | 'yoy';

export interface AnalyticsControlsProps {
  period: Period;
  setPeriod: (p: Period) => void;
  compare: Compare;
  setCompare: (c: Compare) => void;
  storeId?: string | null;
  setStoreId?: (id: string | null) => void;
  storeOptions?: Array<{ id: string; name: string }>;
  compact?: boolean;
}

const periodOpts: Array<{ id: Period; label: string }> = [
  { id: '7d', label: '7일' },
  { id: '30d', label: '30일' },
  { id: 'mom', label: 'MoM' },
  { id: 'yoy', label: 'YoY' },
];

const compareOpts: Array<{ id: Compare; label: string }> = [
  { id: 'prev', label: '이전 기간' },
  { id: 'yoy', label: '전년 동월' },
];

export function AnalyticsControls({
  period,
  setPeriod,
  compare,
  setCompare,
  storeId,
  setStoreId,
  storeOptions,
  compact,
}: AnalyticsControlsProps) {
  const handleStoreChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    setStoreId?.(v === 'all' ? null : v);
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 8 : 12,
        flexWrap: 'wrap',
        padding: compact ? 12 : '14px 16px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-weak)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 2,
          background: 'var(--bg-muted)',
          padding: 3,
          borderRadius: 8,
        }}
      >
        {periodOpts.map((p) => {
          const active = period === p.id;
          return (
            <span
              key={p.id}
              onClick={() => setPeriod(p.id)}
              style={{
                padding: compact ? '5px 10px' : '6px 12px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                background: active ? 'var(--primary-strong)' : 'transparent',
                color: active ? '#fff' : 'var(--fg-2)',
                whiteSpace: 'nowrap',
                transition: 'all 120ms',
              }}
            >
              {p.label}
            </span>
          );
        })}
      </div>
      {storeOptions && storeOptions.length > 0 && (
        <div style={{ position: 'relative' }}>
          <select
            value={storeId ?? 'all'}
            onChange={handleStoreChange}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              padding: compact
                ? '6px 28px 6px 32px'
                : '7px 32px 7px 34px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-base)',
              borderRadius: 8,
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--fg-1)',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="all">전체 매장</option>
            {storeOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <span
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          >
            <Icon name="store" size={14} color="var(--fg-3)" />
          </span>
          <span
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              fontSize: 10,
              color: 'var(--fg-3)',
            }}
          >
            ▾
          </span>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          gap: 2,
          background: 'var(--bg-muted)',
          padding: 3,
          borderRadius: 8,
        }}
      >
        {compareOpts.map((c) => {
          const active = compare === c.id;
          return (
            <span
              key={c.id}
              onClick={() => setCompare(c.id)}
              style={{
                padding: compact ? '5px 10px' : '6px 12px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                background: active ? 'var(--bg-surface)' : 'transparent',
                color: active ? 'var(--fg-1)' : 'var(--fg-3)',
                border: active
                  ? '1px solid var(--border-base)'
                  : '1px solid transparent',
                whiteSpace: 'nowrap',
              }}
            >
              {c.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}
